import {
  AdvancedSearchRequest,
  AdvancedSearchResponse,
  BulkRateTypePayload,
  BulkRateTypeResponse,
  OrdersDetailsModel,
} from 'features/Remittance/types';
import { getPayOrdersWithRangeDirect } from 'api/remittance/remittance-agency-api';
import { handleError } from 'utilities/utilities';
import { getAgencyApiHost, getApiHost } from '../host-by-env';
import { HashUtils } from '@agentnet/utils';

export async function searchReports(
  token: string,
  firmId: string,
  tabName: string,
): Promise<{ result: any; errorCode?: string | number }> {
  const url = `${getApiHost()}remittance/${tabName?.toLowerCase()}/${firmId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function exportReports(files: any, token: string, tabName: string): Promise<any> {
  const url = `${getApiHost()}remittance/${tabName?.toLowerCase()}/export`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(files),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getRateAndFeeData(
  token: string,
  fileId: string,
  type: string,
): Promise<{ result: any; errorCode?: string | number }> {
  const url = `${getApiHost()}remittance/${type}/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function addRemoveQueue(
  token: string,
  payload: { files: string[] },
  type: 'add' | 'remove',
): Promise<{ result: any; errorCode?: string | number }> {
  const url = `${getApiHost()}remittance/report/queue/${type}`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function calculateReports(
  token: string,
  type: string,
  payload: any,
): Promise<{ result: any; errorCode?: string | number }> {
  const url = `${getApiHost()}remittance/${type}/calculate`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function ReportFiles(files: any, token: string): Promise<any> {
  const url = `${getAgencyApiHost()}remittance-service/v1/placeorders`;
  const encodedFileIds: string[] = files?.files ?? [];
  const requestBody = {
    FileId: encodedFileIds.map((id: string) => HashUtils.decode(id)),
    Email: files?.email ?? '',
  };
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  const result: Array<{ FileId: number; OrderNumber: string; FileOrderId: number }> = json.Result ?? [];
  return {
    files: result.map((item) => HashUtils.encode(item.FileId)),
    fileOrderIds: result.map((item) => item.FileOrderId),
    orderNumbers: result.map((item) => item.OrderNumber),
  };
}

export async function getPayInvoiceData(payload: any, token: string): Promise<OrdersDetailsModel> {
  const url = `${getApiHost()}remittance/pay/orders/details`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function orderDetails(orders: any, token: string): Promise<any> {
  const url = `${getApiHost()}remittance/pay/orders/details`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(orders),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getPayOrdersWithRange(token: string, payload: any): Promise<any> {
  return getPayOrdersWithRangeDirect(token, payload);
}

export async function setAutoReport(
  token: string,
  payload: { files: string[]; onHold: boolean },
): Promise<{ result: any; errorCode?: string | number }> {
  const url = `${getApiHost()}remittance/auto-report`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getAdvancedSearchOrders(
  token: string,
  payload: AdvancedSearchRequest,
): Promise<AdvancedSearchResponse[]> {
  const url = `${getApiHost()}remittance/research/search-orders`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getBulkRateTypes(token: string, payload: BulkRateTypePayload): Promise<BulkRateTypeResponse[]> {
  const url = `${getApiHost()}jacket/bulk-rate-types`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}
